import { useCallback, useRef } from 'react';
import type { MonitorTripPlace } from '@/apis/monitor';
import { getColoredMarker } from '../Home/map/MapUtils';
import { formatBoardTime } from './arrivalRows';
import type { LaneEvent } from '../Home/map/laneEvents';
import LaneEventList from './LaneEventList';
import TemperatureLine from './TemperatureLine';
import {
  formatAgo,
  formatDistanceLeft,
  formatLocalTimeAt,
  formatTimeToDestination,
  tripProgress,
} from './tripMath';
import { TEMPERATURE_WINDOW_MS, MonitorTripState } from './useMonitorTrip';

interface TripPanelProps {
  data: MonitorTripState;
  /** The shipment's latest position from the map stream. */
  position: { lat: number; lng: number } | null;
  now: number;
  /** Events on the lane ahead; shown even before the trip data loads. */
  laneEvents?: LaneEvent[];
  /**
   * How far up from the bottom of the screen the panel reaches, in px (0
   * when it is not shown), so the camera can keep the route clear of it.
   */
  onReachChange?: (px: number) => void;
  /** Delayed airports near the shipment (named here, not on the map). */
  airports?: { code: string; delayPct: number }[];
}

/**
 * A callback ref that reports the panel's reach from the bottom of the
 * screen whenever its size changes (the "On this route" strip, the
 * temperature line), and 0 once it is gone.
 */
const useReachFromBottom = (onChange?: (px: number) => void) => {
  const observer = useRef<ResizeObserver | null>(null);
  return useCallback(
    (el: HTMLElement | null) => {
      observer.current?.disconnect();
      observer.current = null;
      if (!onChange) return;
      if (!el) {
        onChange(0);
        return;
      }
      const report = () =>
        onChange(
          Math.max(
            0,
            Math.round(window.innerHeight - el.getBoundingClientRect().top)
          )
        );
      report();
      if (typeof ResizeObserver !== 'undefined') {
        observer.current = new ResizeObserver(report);
        observer.current.observe(el);
      }
    },
    [onChange]
  );
};

const placeLabel = (place: MonitorTripPlace | null): string =>
  place
    ? [place.city, place.state].filter(Boolean).join(', ') || place.name || '—'
    : '—';

/**
 * Seatback-style trip panel for the shipment in focus: where it is between
 * pickup and delivery, and the few numbers a room asks about.
 */
const TripPanel = ({
  data,
  position,
  now,
  laneEvents = [],
  onReachChange,
  airports = [],
}: TripPanelProps) => {
  const measureRef = useReachFromBottom(onReachChange);
  const { trip, track } = data;
  // The map shows airport pins without names: they are named here, so the
  // line shows even before (or without) the trip.
  const airportsLine =
    airports.length > 0 ? (
      <div className='monitor-trip-airports'>
        <span className='monitor-trip-label'>Airports nearby</span>
        <span>
          {airports
            .map(({ code, delayPct }) => `${code} ${Math.round(delayPct)}%`)
            .join(' · ')}
        </span>
      </div>
    ) : null;
  if (!trip) {
    return laneEvents.length > 0 || airportsLine ? (
      <section className='monitor-trip' aria-label='Trip' ref={measureRef}>
        {laneEvents.length > 0 && (
          <LaneEventList events={laneEvents} now={now} />
        )}
        {airportsLine}
      </section>
    ) : null;
  }

  // By distance (see tripProgress), so the bar matches the map.
  const progress = tripProgress({
    pickedUpAt: trip.picked_up_at,
    deliveredAt: trip.delivered_at,
    origin: trip.origin,
    destination: trip.destination,
    path: trip.path,
    position,
  });
  const stats = [
    {
      label: 'Time to destination',
      value: formatTimeToDestination(trip.eta, now),
    },
    {
      label: 'Distance left',
      value: formatDistanceLeft(position, trip.destination, navigator.language),
      sub: 'straight line',
    },
    {
      label: 'Local time at destination',
      value: formatLocalTimeAt(trip.destination?.timezone, now),
    },
    {
      label: 'Last update',
      value: formatAgo(track?.lastUpdateAt ?? null, now),
    },
  ].filter((stat) => stat.value);

  return (
    <section className='monitor-trip' aria-label='Trip' ref={measureRef}>
      <div className='monitor-trip-route'>
        <div className='monitor-trip-end'>
          <span className='monitor-trip-label'>{placeLabel(trip.origin)}</span>
          <span>
            {trip.picked_up_at
              ? `Picked up ${formatBoardTime(trip.picked_up_at)}`
              : 'Not picked up yet'}
          </span>
        </div>
        <div className='monitor-trip-bar'>
          <div className='monitor-trip-track'>
            <div
              className='monitor-trip-fill'
              // eslint-disable-next-line react/forbid-dom-props -- the width is the share travelled, known only at run time
              style={{ width: `${Math.round((progress ?? 0) * 100)}%` }}
            />
          </div>
          {progress !== null && (
            <img
              className='monitor-trip-vehicle'
              // The stream carries no transport mode, so the map shows trucks too.
              src={getColoredMarker('truck', '#1565C0', false, false)}
              alt=''
              // eslint-disable-next-line react/forbid-dom-props -- the vehicle sits at the share travelled, known only at run time
              style={{ left: `${Math.round(progress * 100)}%` }}
            />
          )}
        </div>
        <div className='monitor-trip-end monitor-trip-end--right'>
          <span className='monitor-trip-label'>
            {placeLabel(trip.destination)}
          </span>
          {trip.eta && <span>ETA {formatBoardTime(trip.eta)}</span>}
          {/* The delivery commitment, when the ETA has moved off it. */}
          {trip.scheduled_delivery_at &&
            Date.parse(trip.scheduled_delivery_at) !==
              Date.parse(trip.eta ?? '') && (
              <span className='monitor-trip-scheduled'>
                Scheduled {formatBoardTime(trip.scheduled_delivery_at)}
              </span>
            )}
        </div>
      </div>
      {stats.length > 0 && (
        <div className='monitor-trip-stats'>
          {stats.map((stat) => (
            <div key={stat.label} className='monitor-trip-stat'>
              <span className='monitor-trip-label'>{stat.label}</span>
              <span className='monitor-trip-value'>{stat.value}</span>
              {stat.sub && <span className='monitor-trip-sub'>{stat.sub}</span>}
            </div>
          ))}
        </div>
      )}
      {airportsLine}
      {track && track.readings.t.length > 0 && (
        <TemperatureLine
          readings={track.readings}
          band={trip.temperature}
          from={now - TEMPERATURE_WINDOW_MS}
          to={now}
        />
      )}
      <LaneEventList events={laneEvents} now={now} />
    </section>
  );
};

export default TripPanel;
